import React, { useState, useEffect, useCallback } from 'react';
import { Card, Badge } from '../../../components/ui';
import { RefreshCw, AlertCircle, ArrowUpCircle, ArrowDownCircle, DollarSign } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { diaOperativo, formatTimeCR } from '../../../utils/date';

// ─── Tipos ───────────────────────────────────────────────

interface MovimientoItem {
  id: string;
  tipo: string;
  naturaleza: string;
  monto: string;
  descripcion: string;
  referencia_tipo: string | null;
  referencia_id: string | null;
  fecha_operativa: string | null;
  created_at: string | null;
}

interface HistoricoResponse {
  items: MovimientoItem[];
  total_ingresos: string;
  total_egresos: string;
  saldo_periodo: string;
  fecha_desde: string;
  fecha_hasta: string;
}

// ─── Mapas de presentación ───────────────────────────────

const TIPO_LABELS: Record<string, string> = {
  VENTA: 'Venta',
  VENTA_CREDITO: 'Venta crédito',
  PREMIO: 'Premio',
  PREMIO_PAGADO: 'Premio pagado',
  ANULACION_VENTA: 'Anulación',
  ANULACION_VENTA_CREDITO: 'Anul. crédito',
  ABONO_CREDITO: 'Abono crédito',
  ABONO_CREDITO_LOCAL: 'Abono local',
  ABONO_CREDITO_SINPE: 'Abono SINPE',
  ABONO_CREDITO_TRANSFERENCIA: 'Abono transf.',
  COMPRA_RECARGA: 'Recarga',
  INGRESO_EXTRA: 'Ingreso extra',
  VALE: 'Vale',
  AJUSTE_INGRESO: 'Ajuste (+)',
  AJUSTE_EGRESO: 'Ajuste (-)',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  VENTA: 'primary',
  VENTA_CREDITO: 'neutral',
  PREMIO: 'warning',
  PREMIO_PAGADO: 'warning',
  ANULACION_VENTA: 'danger',
  ANULACION_VENTA_CREDITO: 'danger',
  ABONO_CREDITO: 'success',
  ABONO_CREDITO_LOCAL: 'success',
  ABONO_CREDITO_SINPE: 'success',
  ABONO_CREDITO_TRANSFERENCIA: 'success',
  AJUSTE_INGRESO: 'success',
  AJUSTE_EGRESO: 'danger',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return `₡${Math.abs(n).toLocaleString('es-CR', { minimumFractionDigits: 0 })}`;
};

// ─── Componente principal ────────────────────────────────

export const LocalHistoricoPage: React.FC = () => {
  const hoy = diaOperativo();

  // Fecha por defecto: últimos 7 días
  const hace7 = (() => {
    const d = new Date(hoy + 'T00:00:00');
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  })();

  const [fechaDesde, setFechaDesde] = useState(hace7);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [data, setData] = useState<HistoricoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Validación de rango
  const rangoError = (() => {
    if (!fechaDesde || !fechaHasta) return 'Seleccione ambas fechas';
    if (fechaDesde > fechaHasta) return 'La fecha inicio no puede ser mayor a la fecha fin';
    const inicio = new Date(fechaDesde + 'T00:00:00');
    const fin = new Date(fechaHasta + 'T00:00:00');
    const diffDias = Math.round((fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDias > 30) return 'El rango máximo es de 30 días';
    return null;
  })();

  const cargarDatos = useCallback(async () => {
    if (rangoError) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        limit: '200',
      });
      const res = await fetchAPI<HistoricoResponse>(`/operaciones/local/historico?${params}`);
      setData(res);
    } catch (e: any) {
      setError(e.message || 'Error al cargar historial');
    } finally {
      setLoading(false);
    }
  }, [fechaDesde, fechaHasta, rangoError]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const movimientos = data?.items || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Historial</h1>
          <p className="text-gray-500">Movimientos financieros del local</p>
        </div>
        <button
          onClick={cargarDatos}
          disabled={loading || !!rangoError}
          className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
        {rangoError && (
          <p className="mt-2 text-sm text-red-600">{rangoError}</p>
        )}
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Resumen del periodo */}
      {data && !loading && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <div className="text-center">
              <ArrowUpCircle className="mx-auto text-green-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Ingresos</p>
              <p className="text-xl font-bold text-green-600">{formatMonto(data.total_ingresos)}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <ArrowDownCircle className="mx-auto text-red-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Egresos</p>
              <p className="text-xl font-bold text-red-600">{formatMonto(data.total_egresos)}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <DollarSign className="mx-auto text-blue-600 mb-2" size={24} />
              <p className="text-sm text-gray-500">Neto</p>
              <p className={`text-xl font-bold ${Number(data.saldo_periodo) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatMonto(data.saldo_periodo)}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Tabla de movimientos */}
      {!loading && data && (
        <Card padding="none">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-medium text-gray-700">
              Movimientos ({movimientos.length})
            </h3>
          </div>
          {movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos en este periodo</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Fecha</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-700">Tipo</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700">Monto</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 hidden sm:table-cell">Descripcion</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700 hidden md:table-cell">Hora</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {movimientos.map((m) => {
                    const esIngreso = m.naturaleza === 'INGRESO';
                    return (
                      <tr key={m.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">{m.fecha_operativa || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'} size="sm">
                            {TIPO_LABELS[m.tipo] || m.tipo}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-medium whitespace-nowrap ${esIngreso ? 'text-green-600' : 'text-red-600'}`}>
                          {esIngreso ? '+' : '-'}{formatMonto(m.monto)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate hidden sm:table-cell">
                          {m.descripcion}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap hidden md:table-cell">
                          {m.created_at ? formatTimeCR(m.created_at) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <div className="text-center py-12 text-gray-400">
            <RefreshCw size={32} className="mx-auto mb-3 animate-spin" />
            <p>Cargando historial...</p>
          </div>
        </Card>
      )}
    </div>
  );
};

export default LocalHistoricoPage;
